import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import { Col, Row, Form } from 'react-bootstrap';
import DropdownChartSpeed from './DropdownChartSpeed';
import DropdownChartAmp from './DropdownChartAmp';
import { EcgPreviewChart } from './EcgPreviewChart';
import { useTranslation } from 'react-i18next';
import mergeDeep from '../../../utils/mergeDeep';
import ButtonChartMarkers from './ButtonChartMarkers';
import EcgNavigationCanvas from './EcgNavigationCanvas';
import { BodyPositionFetcher } from '../Processed/BodyPositionFetcher';
import { NAV_UI } from '../../../config/navigationConfig';
import api from '../../../services/api';
import MarkerEditingToolbar from './MarkerEditingToolbar';
import { R_PEAK_TYPE } from './MarkerEditingManager';

const EcgHighchartsAsyncWithNewNavForwardRef = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const chartComponentRef = useRef(null);
  const [viewSpeed, setViewSpeed] = useState(40); // Default 40ms/mm (25mm/s)
  const navigationCanvasRef = useRef(null);
  const bodyPositionRef = useRef(null); // imperative control for body position fetcher

  // Online preview mode flag: when ON, display all online data immediately; when OFF, user scrolled away
  const [isOnlinePreviewMode, setIsOnlinePreviewMode] = useState(false);

  // Marker editing state
  const [isEditMarkersMode, setIsEditMarkersMode] = useState(false);
  const [areMarkersVisible, setAreMarkersVisible] = useState(false);
  const [selectedMarkerType, setSelectedMarkerType] = useState(R_PEAK_TYPE);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  // Refs to track pending markers (avoids state updates that cause re-renders)
  const pendingMarkerAdditionsRef = useRef([]); // [{x: number, type: number}]
  const pendingMarkerDeletionsRef = useRef([]);

  //const EXMAPLE_SERIES_ID = 'mv-ms-test';

  // Online state constants (matching BaseMultipleChartsView)
  const ONLINE_STATE_NO_ONLINE = 0;
  const ONLINE_STATE_WAITING = 10;
  const ONLINE_STATE_RECEIVING = 20;
  const ONLINE_STATE_FINISHED = 30;

  useImperativeHandle(ref, () => ({
    setInitialData(data, seriesSettings) {
      chartComponentRef.current.setInitialData(data, seriesSettings);
    },
    setInitialDataForLongSeries(longData, asyncDataLoadUrl, seriesSettings, initialLoadRange) {
      chartComponentRef.current.setInitialDataForLongSeries(longData, asyncDataLoadUrl, seriesSettings, initialLoadRange);
    },
    setDataViewMode(mode) {
      chartComponentRef.current.setDataViewMode(mode);
    },
    addNewDataToChart(data, seriesId) {
      chartComponentRef.current.addNewDataToChart(data, seriesId);
    },
    addNewDataToChartLongSeries(data) {
      console.log('EcgHighchartsAsyncWithNewNav: Online preview mode =', isOnlinePreviewMode, 'hasNavigationChunk =', !!data?.navigationChunk);

      // Pass navigation chunk to the navigation canvas if it exists
      if (data?.navigationChunk && navigationCanvasRef.current?.logNavigationChunkForOnlineMode) {
        console.log('EcgHighchartsAsyncWithNewNav: Forwarding navigation chunk to canvas');
        navigationCanvasRef.current.logNavigationChunkForOnlineMode(data.navigationChunk);
      } else if (!data?.navigationChunk) {
        console.log('EcgHighchartsAsyncWithNewNav: No navigation chunk in data');
      }

      chartComponentRef.current.addNewDataToChartLongSeries(data)
    },
    setOnlinePreviewMode(enabled) {
      setIsOnlinePreviewMode(enabled);
    },
    setupYAxis(settings) {
      chartComponentRef.current.setupYAxis(settings);
    },
    scrollToEnd() {
      // Complete sequence for enabling online viewing:
      // 1. Set isLastRow flag (to avoid race conditions)
      // 2. Enable force auto-scroll (ensures next data triggers scroll)
      // 3. Navigate to last row (with scrollToLastPoint=true)
      console.log('=== EcgHighchartsAsyncWithNewNav.scrollToEnd CALLED ===');

      if (chartComponentRef.current) {
        console.log('EcgHighchartsAsyncWithNewNav: Setting isLastRow flag');
        // Explicitly set flag to ensure it's true even if navigation is cached/delayed
        chartComponentRef.current.setIsLastRow(true);

        console.log('EcgHighchartsAsyncWithNewNav: Enabling force auto-scroll');
        // Enable force auto-scroll for future data
        chartComponentRef.current.enableForceAutoScroll();
      } else {
        console.warn('EcgHighchartsAsyncWithNewNav: chartComponentRef.current is null!');
      }

      if (navigationCanvasRef.current) {
        console.log('EcgHighchartsAsyncWithNewNav: Calling navigateToLastRow');
        // This will trigger handleNavigationClick with scrollToLastPoint=true
        navigationCanvasRef.current.navigateToLastRow();
      } else {
        console.warn('EcgHighchartsAsyncWithNewNav: navigationCanvasRef.current is null!');
      }

      console.log('=== EcgHighchartsAsyncWithNewNav.scrollToEnd END ===');
    },
    enableForceAutoScroll() {
      chartComponentRef.current.enableForceAutoScroll();
    },
    addAnnotation(newAnnotation) {
      chartComponentRef.current.addAnnotation(newAnnotation);
    },
    setPlotLinesX(plotLinesX) {
      chartComponentRef.current.setPlotLinesX(plotLinesX);
    },
    moveChartToPosition(pointStart) {
      chartComponentRef.current.moveChartToPosition(pointStart);
    },
    clearAllChartData() {
      chartComponentRef.current.clearAllChartData();
    },
    updateComments(comments) {
      chartComponentRef.current.updateComments(comments);
    }
  }));

  useEffect(() => {
    // Component initialization - viewport will be set by chart
  }, []);

  // Automatically enable online preview mode when record enters online/pending state
  useEffect(() => {
    if (props.onlineTransmissionState === ONLINE_STATE_WAITING ||
        props.onlineTransmissionState === ONLINE_STATE_RECEIVING) {
      setIsOnlinePreviewMode(true);
      console.log('EcgHighchartsAsyncWithNewNav: Online preview mode enabled automatically');
    }
  }, [props.onlineTransmissionState]);

  //const exampleSeries = (offsetNull) => {
  //  let offsetX = offsetNull ?? 0;
  //  let offsetY = 0;
  //  let heightY = 1;

  //  return {
  //    id: EXMAPLE_SERIES_ID,
  //    name: '1mV, 2.5Hz',
  //    type: 'line',
  //    data: [
  //      [offsetX + 0, offsetY],
  //      [offsetX + 80, offsetY],
  //      [offsetX + 80, offsetY + heightY],
  //      [offsetX + 280, offsetY + heightY],
  //      [offsetX + 280, offsetY],
  //      [offsetX + 480, offsetY],
  //      [offsetX + 480, offsetY + heightY],
  //      [offsetX + 680, offsetY + heightY],
  //      [offsetX + 680, offsetY],
  //      [offsetX + 760, offsetY],
  //    ],
  //    color: 'blue',
  //    lineWidth: 2,
  //    showInNavigator: false
  //  }
  //};

  // Memoize all callbacks passed to EcgPreviewChart to prevent unnecessary re-renders
  const runOnChartLoadEvent = useCallback((chart) => {
    //setTimeout(() => { try { removeAndAddExampleSeries(chart) } catch (error) { } }, 2000);
  }, []);

  //const removeAndAddExampleSeries = (chart, startPoint) => {
  //  if (props.hideCalibrationSeries === undefined || (props.hideCalibrationSeries !== undefined && !props.hideCalibrationSeries)) {
  //    if (chart.get(EXMAPLE_SERIES_ID))
  //      chart.get(EXMAPLE_SERIES_ID).update(exampleSeries(startPoint ?? 0));
  //    else
  //      chart.addSeries(exampleSeries());
  //  }
  //};

  const accumulationModeSetCallback = useCallback((chart, startTime) => {
    //setTimeout(() => removeAndAddExampleSeries(chart, startTime), 500);
  }, []);

  const refreshModeSetCallback = useCallback((chart, startTime) => {
    //setTimeout(() => removeAndAddExampleSeries(chart, 0), 500);
  }, []);

  // Handle navigation click
  const handleNavigationClick = (viewStartTimeMs, startTimeMs, endTimeMs, centerTimeMs, isLastRow, scrollToLastPoint) => {
    console.log('=== EcgHighchartsAsyncWithNewNav.handleNavigationClick CALLED ===');
    console.log('handleNavigationClick params:', {
      viewStartTimeMs,
      startTimeMs,
      endTimeMs,
      centerTimeMs,
      isLastRow,
      scrollToLastPoint
    });

    if (chartComponentRef.current) {
      // If requested, scroll to last data point after data loads
      if (scrollToLastPoint) {
        console.log('handleNavigationClick: scrollToLastPoint=true, calling requestScrollToLastPoint');
        chartComponentRef.current.requestScrollToLastPoint();
      } else {
        console.log('handleNavigationClick: scrollToLastPoint=false, skipping requestScrollToLastPoint');
      }

      console.log('handleNavigationClick: Calling loadDataChunk');
      // Call loadDataChunk with the times already calculated by EcgNavigationCanvas
      // isLastRow=true means endTimeMs represents the actual end of navigation data
      chartComponentRef.current.loadDataChunk(startTimeMs, endTimeMs, centerTimeMs, isLastRow);
      // The chart will report its new viewport via onViewportChanged
    } else {
      console.warn('handleNavigationClick: chartComponentRef.current is null!');
    }

    console.log('=== EcgHighchartsAsyncWithNewNav.handleNavigationClick END ===');
  };

  // Handle viewport changes reported by the chart
  const handleViewportChanged = useCallback((minMs, maxMs) => {
    console.log('handleViewportChanged with ', minMs, maxMs);
    // Notify navigation canvas directly without causing any re-renders
    if (navigationCanvasRef.current && navigationCanvasRef.current.updateViewport) {
      navigationCanvasRef.current.updateViewport(minMs, maxMs);
    }
    if (bodyPositionRef.current && bodyPositionRef.current.setRangeMs) {
      bodyPositionRef.current.setRangeMs({ min: minMs, max: maxMs });
    }
  }, []);

  // Handle when loading is complete (success, error, or cached)
  const handleLoadComplete = useCallback(() => {
    if (navigationCanvasRef.current && navigationCanvasRef.current.clearLoadingState) {
      navigationCanvasRef.current.clearLoadingState();
    }
  }, []);

  // Handle when user navigates away from the last row
  const handleUserNavigatedAway = useCallback(() => {
    console.log('EcgHighchartsAsyncWithNewNav: User navigated away - disabling online preview mode');
    setIsOnlinePreviewMode(false);
  }, []);
  
  // Track extremes changes (legacy) - memoized to prevent chart re-renders
  const handleExtremesChanged = useCallback((min, max) => {
    // Call parent callback if provided
    if (props.extremesChangedCallback) {
      props.extremesChangedCallback(min, max);
    }
  }, [props.extremesChangedCallback]);

  const ecgChartOptions = {
    // To avoid unnecessary update keep all options in the state.
    chart: {
      height: '400px',
      scrollablePlotArea: {
        minWidth: 600,
        scrollPositionX: 0
      }
    },
    yAxis: {
      minorTicks: true,
      minorTickInterval: 0.1,
      minorGridLineColor: 'red',
      minorGridLineWidth: 0.3,

      tickInterval: 1,
      gridLineWidth: 1.5,
      gridLineColor: 'red',

      min: -3,
      max: 3,
      scrollbar: {
        enabled: true
      },
      zoomEnabled: false,
    },
    xAxis: {
      minorTicks: true,
      minorTickInterval: 40,
      minorGridLineColor: 'red',
      minorGridLineWidth: 0.3,

      tickInterval: 400,
      gridLineWidth: 1.5,
      gridLineColor: 'red',

      zoomEnabled: false,

      maxRange: undefined,

      dateTimeLabelFormats: {
        millisecond: '%M:%S.%L',
        second: '%M:%S.%L',
        minute: '%M:%S.%L',
        hour: '%M:%S.%L',
        day: '%M:%S.%L',
        week: '%M:%S.%L',
        month: '%M:%S.%L',
        year: '%Y'
      },
      allowDecimals: true,
      scrollbar: {
        enabled: true,
        liveRedraw: true  // Enable smooth scrolling while dragging
      }
    },
    rangeSelector: {
      enabled: false
    },
    navigator: {
      enabled: false  // Disable the built-in navigator
    },
    //series: [
    //],
    tooltip: {
      valueDecimals: 3,
      valueSuffix: ' mV',
      split: true,
      shared: false,
      xDateFormat: '%M:%S.%L',
    },
    plotOptions: {
      series: {
        point: {
          events: {
          }
        }
      },
      line: {
        dataGrouping: {
          dateTimeLabelFormats: {
            millisecond: [
              '%M:%S.%L', '%M:%S.%L', ''
            ],
            second: ['%M:%S.%L', '%M:%S.%L', ''],
            minute: ['%M:%S.%L', '%M:%S.%L', ''],
            hour: ['%M:%S.%L', '%M:%S.%L', ''],
            day: ['%M:%S.%L', '%M:%S.%L', ''],
            week: ['%M:%S.%L', '%M:%S.%L', ''],
            month: ['%M:%S.%L', '%M:%S.%L', ''],
            year: ['%M:%S.%L', '%M:%S.%L', ''],
          }
        }
      }
    },
    title: {
      text: ''
    }
  };

  const ecgNavigatorChartOptions = {
    navigator: {
      enabled: false  // Disable navigator completely for new navigation
    },
    title: {
      text: ''
    }
  };

  // Memoize chart options to prevent unnecessary recreations
  const chartOptions = useMemo(() => {
    let common = ecgChartOptions;
    if (props.specificChartOptions) {
      return mergeDeep(common, props.specificChartOptions);
    }
    return common;
  }, [props.specificChartOptions]); // Only recreate if specificChartOptions changes

  const navigatorOptions = useMemo(() => {
    return ecgNavigatorChartOptions;
  }, []); // Navigator options never change

  const handleSpeedChange = (msPerMm) => {
    setViewSpeed(msPerMm);
    console.log('Speed changed to:', msPerMm, 'ms/mm');

    // Tell the chart to update its extremes
    // The chart will report the new viewport via onViewportChanged
    chartComponentRef.current.setChartExtremesX(msPerMm);
  };

  // Helper to update hasPendingChanges state based on refs
  const updatePendingChangesState = useCallback(() => {
    const hasChanges = pendingMarkerAdditionsRef.current.length > 0 || pendingMarkerDeletionsRef.current.length > 0;
    setHasPendingChanges(hasChanges);
  }, []);

  // Marker editing callbacks - use refs to track changes, update hasPendingChanges state for UI
  const handleMarkerAdded = useCallback((marker) => {
    console.log('Marker added:', marker);
    pendingMarkerAdditionsRef.current = [...pendingMarkerAdditionsRef.current, marker];
    updatePendingChangesState();
    // Update chart display imperatively (no re-render)
    if (chartComponentRef.current) {
      chartComponentRef.current.addPendingMarker(marker);
    }
  }, [updatePendingChangesState]);

  const handleMarkerDeleted = useCallback((marker) => {
    console.log('Marker deleted:', marker);
    // Check if this marker was in pendingAdditions (user added then deleted it)
    const wasAddedInThisSession = pendingMarkerAdditionsRef.current.some(m => m.x === marker.x && m.type === marker.type);
    if (wasAddedInThisSession) {
      // Remove from additions instead of adding to deletions
      pendingMarkerAdditionsRef.current = pendingMarkerAdditionsRef.current.filter(m => !(m.x === marker.x && m.type === marker.type));
    } else {
      // Add to deletions (it's an original marker from server)
      pendingMarkerDeletionsRef.current = [...pendingMarkerDeletionsRef.current, marker];
    }
    updatePendingChangesState();
    // Update chart display imperatively (no re-render)
    if (chartComponentRef.current) {
      chartComponentRef.current.removePendingMarker(marker);
    }
  }, [updatePendingChangesState]);

  const handleSaveMarkers = useCallback(async () => {
    console.log('=== SAVE MARKERS ===');
    console.log('Pending additions:', pendingMarkerAdditionsRef.current);
    console.log('Pending deletions:', pendingMarkerDeletionsRef.current);
    console.log('====================');

    // Map markers with numeric type (EcgAnnotationTypes value), default to R (48)
    const addedMarkers = pendingMarkerAdditionsRef.current.map(m => ({
      x: m.x,
      type: typeof m.type === 'number' ? m.type : R_PEAK_TYPE
    }));
    const deletedMarkers = pendingMarkerDeletionsRef.current.map(m => ({
      x: m.x,
      type: typeof m.type === 'number' ? m.type : R_PEAK_TYPE
    }));

    if (addedMarkers.length === 0 && deletedMarkers.length === 0) {
      // No changes to save
      setIsEditMarkersMode(false);
      setHasPendingChanges(false);
      if (chartComponentRef.current) {
        chartComponentRef.current.setEditMode(false);
      }
      return;
    }

    try {
      await api.put(`${props.baseRecordUrl}/markers`, {
        addedMarkers,
        deletedMarkers
      });

      // Success - clear pending changes and exit edit mode
      pendingMarkerAdditionsRef.current = [];
      pendingMarkerDeletionsRef.current = [];
      setIsEditMarkersMode(false);
      setHasPendingChanges(false);
      if (chartComponentRef.current) {
        chartComponentRef.current.setEditMode(false);
      }
    } catch (error) {
      console.error('Failed to save markers:', error);
      alert(error.response?.data?.message || error.message || 'Failed to save markers');
    }
  }, [props.baseRecordUrl]);

  const handleToggleEditMode = useCallback((enabled) => {
    setIsEditMarkersMode(enabled);
    // Update chart edit mode imperatively (no re-render of chart)
    if (chartComponentRef.current) {
      chartComponentRef.current.setEditMode(enabled);
      // Pass selected marker type to chart when entering edit mode
      if (enabled && chartComponentRef.current.setSelectedMarkerType) {
        chartComponentRef.current.setSelectedMarkerType(selectedMarkerType);
      }
    }
    if (!enabled) {
      // If turning off edit mode, clear pending changes
      pendingMarkerAdditionsRef.current = [];
      pendingMarkerDeletionsRef.current = [];
      setHasPendingChanges(false);
      setSelectedMarkerType(R_PEAK_TYPE); // Reset to default
    }
  }, [selectedMarkerType]);

  // Handle marker type change from toolbar
  const handleMarkerTypeChange = useCallback((type) => {
    setSelectedMarkerType(type);
    // Update chart's selected marker type
    if (chartComponentRef.current && chartComponentRef.current.setSelectedMarkerType) {
      chartComponentRef.current.setSelectedMarkerType(type);
    }
  }, []);

  return (
    <>
      {props.showNavigation !== false && (
        <Row>
          <Col>
            <EcgNavigationCanvas
              ref={navigationCanvasRef}
              baseRecordUrl={props.baseRecordUrl}
              initialViewStartMs={0}
              initialViewEndMs={5000}
              onNavigationClick={handleNavigationClick}
              useActualTime={props.useActualTime}
              isOnlineMode={isOnlinePreviewMode}
              onlineTransmissionState={props.onlineTransmissionState}
              onUserNavigatedAway={handleUserNavigatedAway}
            />
          </Col>
        </Row>
      )}
      
      {(props.isHideControls === undefined || props.isHideControls === false) &&
        <Row className="align-items-center">
          {/* Body position icon to the left of the scale and speed */}
          <Col lg="auto" className="mt-2 d-flex align-items-center">
            <BodyPositionFetcher 
              ref={bodyPositionRef}
              baseUrl={props.baseRecordUrl}
              scale={NAV_UI.bodyPositionScale}
            />
          </Col>
          <Col lg className="mt-2">
            <DropdownChartAmp onSelectionChangedCallback={(value) => chartComponentRef.current.setChartExtremesY(value)} />
          </Col>
          <Col lg className="mt-2">
            <DropdownChartSpeed onSelectionChangedCallback={handleSpeedChange} />
          </Col>
          <Col lg className="mt-2">
            <ButtonChartMarkers onSelectionChangedCallback={(value) => {
              chartComponentRef.current.setMarkersVisibility(value);
              setAreMarkersVisible(value);
              // Turn off edit mode when markers are hidden
              if (!value && isEditMarkersMode) {
                handleToggleEditMode(false);
              }
            }} />
          </Col>
          {props.allowEditMarkers && areMarkersVisible && (
            <Col lg="auto" className="mt-2 d-flex align-items-center">
              <Form.Check
                inline
                type="switch"
                id="edit-markers-switch"
                label={t('recordView.multiChart.ecg.editMarkers')}
                className="ms-3 text-nowrap"
                checked={isEditMarkersMode}
                onChange={(e) => handleToggleEditMode(e.target.checked)}
              />
            </Col>
          )}
          <Col lg>
          </Col>
        </Row>
      }

      {/* Marker editing toolbar - appears when edit mode is active */}
      {props.allowEditMarkers && isEditMarkersMode && (
        <Row className="mt-2">
          <Col>
            <MarkerEditingToolbar
              selectedMarkerType={selectedMarkerType}
              onMarkerTypeChange={handleMarkerTypeChange}
              onSave={handleSaveMarkers}
              onCancel={() => handleToggleEditMode(false)}
              hasPendingChanges={hasPendingChanges}
            />
          </Col>
        </Row>
      )}

      <Row>
        <Col className="px-0">
          <div style={{ position: 'relative' }}>
            <EcgPreviewChart
              ref={chartComponentRef}
              baseRecordUrl={props.baseRecordUrl}
              useActualTime={props.useActualTime}
              onViewportChanged={handleViewportChanged}
              onLoadComplete={handleLoadComplete}
              specificChartOptions={chartOptions}
              seriesName={t('recordView.multiChart.seriesName.ecg')}
              onChartLoadEvent={runOnChartLoadEvent}
              defaultCellTimeXms={40}
              accumulationModeSetCallback={accumulationModeSetCallback}
              refreshModeSetCallback={refreshModeSetCallback}
              isStockChart={props.isStockChart}
              extremesChangedCallback={handleExtremesChanged}
              hideCalibrationSeries={props.hideCalibrationSeries}
              onMarkerAdded={handleMarkerAdded}
              onMarkerDeleted={handleMarkerDeleted}
              onRPeakClick={props.onRPeakClick}
            />
            {props.children}
          </div>
        </Col>
      </Row>
    </>
  )


});

// Set display name for debugging
EcgHighchartsAsyncWithNewNavForwardRef.displayName = 'EcgHighchartsAsyncWithNewNavForwardRef';

export const EcgHighchartsAsyncWithNewNav = React.memo(EcgHighchartsAsyncWithNewNavForwardRef);
